/**
 * Biblioteca de Minerales — Carousel Controller
 * Extracted from biblioteca-minerales.liquid (Phase 2 refactor)
 *
 * Handles carousel pagination, navigation arrows, dots, keyboard nav,
 * scroll-sync, entrance animation, and responsive recalc with debounce.
 *
 * Phase 3: Added AbortController cleanup to prevent memory leaks.
 * Cached getComputedStyle call to reduce layout thrashing.
 *
 * Usage: initBibliotecaCarousel(containerElement)
 */
(function () {
  'use strict';

  window.initBibliotecaCarousel = function (container) {
    if (!container) return;

    /* ── Cleanup previous instance if re-initialized ── */
    if (container._cleanupCarousel) {
      container._cleanupCarousel();
    }

    var ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var signal = ac ? ac.signal : null;
    var resizeObs = null;
    var intersectionObs = null;

    function addListener(target, evt, handler, opts) {
      opts = opts || {};
      if (signal) opts.signal = signal;
      target.addEventListener(evt, handler, opts);
    }

    var track = container.querySelector('.mp-bib-track');
    var scrollerWrap = container.querySelector('.mp-bib-scroller');
    var prevBtn = container.querySelector('.mp-bib-nav.prev');
    var nextBtn = container.querySelector('.mp-bib-nav.next');
    var dotsWrap = container.querySelector('.mp-bib-dots');
    var liveRegion = container.querySelector('[aria-live]');
    var cards = Array.prototype.slice.call(track.querySelectorAll('.mp-bib-card'));
    var totalCards = cards.length;

    if (!totalCards) return;

    var visibleCount = 1;
    var currentPage = 0;
    var totalPages = 1;
    var cachedGap = 20;
    var scrollEl = track;
    var lastCalcWidth = null;

    /* ── Helpers ── */

    function pickScrollEl() {
      if (scrollerWrap && scrollerWrap.scrollWidth > scrollerWrap.clientWidth + 1) {
        scrollEl = scrollerWrap;
      } else {
        scrollEl = track;
      }
    }

    function refreshGap() {
      /* Only recalculate if width actually changed */
      var currentWidth = scrollEl.clientWidth;
      if (currentWidth !== lastCalcWidth) {
        cachedGap = parseFloat(getComputedStyle(track).gap) || 20;
        lastCalcWidth = currentWidth;
      }
    }

    /* ── Pagination calc ── */

    function calcPages() {
      if (!cards[0]) return;
      pickScrollEl();
      refreshGap();
      var cardW = cards[0].offsetWidth;
      var visW = scrollEl.clientWidth;
      visibleCount = Math.max(1, Math.floor((visW + cachedGap) / (cardW + cachedGap)));
      totalPages = Math.max(1, Math.ceil(totalCards / visibleCount));
      if (currentPage >= totalPages) currentPage = totalPages - 1;
      buildDots();
      updateArrows();
      updateLive();
    }

    /* ── Dots ── */

    function buildDots() {
      while (dotsWrap.firstChild) dotsWrap.removeChild(dotsWrap.firstChild);
      for (var i = 0; i < totalPages; i++) {
        var d = document.createElement('button');
        d.className = 'mp-bib-dot' + (i === currentPage ? ' active' : '');
        d.setAttribute('aria-label', 'Pagina ' + (i + 1) + ' de ' + totalPages);
        if (i === currentPage) d.setAttribute('aria-current', 'true');
        d.dataset.page = i;
        addListener(d, 'click', function () {
          goToPage(parseInt(this.dataset.page, 10));
        });
        dotsWrap.appendChild(d);
      }
    }

    function updateDots() {
      var dots = dotsWrap.querySelectorAll('.mp-bib-dot');
      for (var j = 0; j < dots.length; j++) {
        var isActive = j === currentPage;
        dots[j].className = 'mp-bib-dot' + (isActive ? ' active' : '');
        if (isActive) dots[j].setAttribute('aria-current', 'true');
        else dots[j].removeAttribute('aria-current');
      }
    }

    /* ── Live region ── */

    var liveTimer = null;
    function updateLive() {
      if (!liveRegion) return;
      clearTimeout(liveTimer);
      liveTimer = setTimeout(function () {
        liveRegion.textContent = 'Pagina ' + (currentPage + 1) + ' de ' + totalPages;
      }, 300);
    }

    /* ── Arrows ── */

    function updateArrows() {
      if (prevBtn) prevBtn.disabled = currentPage === 0;
      if (nextBtn) nextBtn.disabled = currentPage >= totalPages - 1;
    }

    /* ── Navigation ── */

    function goToPage(page) {
      if (page < 0 || page >= totalPages) return;
      currentPage = page;
      var cardW = cards[0].offsetWidth;
      var scrollTarget = page * visibleCount * (cardW + cachedGap);
      scrollEl.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      updateDots();
      updateArrows();
      updateLive();
    }

    /* ── Scroll sync (rAF-throttled) ── */

    var rafId = null;
    function onScroll() {
      if (rafId) return; /* skip if already scheduled */
      rafId = requestAnimationFrame(function () {
        rafId = null;
        var cardW = cards[0].offsetWidth;
        var page = Math.round(scrollEl.scrollLeft / (visibleCount * (cardW + cachedGap)));
        page = Math.max(0, Math.min(page, totalPages - 1));
        if (page !== currentPage) {
          currentPage = page;
          updateDots();
          updateArrows();
          updateLive();
        }
      });
    }

    addListener(track, 'scroll', onScroll, { passive: true });
    if (scrollerWrap) addListener(scrollerWrap, 'scroll', onScroll, { passive: true });

    /* ── Arrow buttons ── */

    if (prevBtn) addListener(prevBtn, 'click', function () { goToPage(currentPage - 1); });
    if (nextBtn) addListener(nextBtn, 'click', function () { goToPage(currentPage + 1); });

    /* ── Keyboard navigation ── */

    addListener(track, 'keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'Right') { e.preventDefault(); goToPage(currentPage + 1); }
      else if (e.key === 'ArrowLeft' || e.key === 'Left') { e.preventDefault(); goToPage(currentPage - 1); }
      else if (e.key === 'Home') { e.preventDefault(); goToPage(0); }
      else if (e.key === 'End') { e.preventDefault(); goToPage(totalPages - 1); }
    });

    /* ── Debounced ResizeObserver ── */

    var resizeTimer = null;
    function debouncedCalcPages() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calcPages, 150);
    }

    if ('ResizeObserver' in window) {
      resizeObs = new ResizeObserver(debouncedCalcPages);
      resizeObs.observe(scrollerWrap || track);
    } else {
      addListener(window, 'resize', debouncedCalcPages);
    }

    /* ── Entrance animation ── */

    if (!(window.Shopify && window.Shopify.designMode)) {
      container.classList.add('mp-bib-animate');
      if ('IntersectionObserver' in window) {
        intersectionObs = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) {
            container.classList.add('mp-visible');
            intersectionObs.disconnect();
            intersectionObs = null;
          }
        }, { threshold: 0.15 });
        intersectionObs.observe(container);
      } else {
        container.classList.add('mp-visible');
      }
    }

    /* ── Initial calc ── */
    calcPages();

    /* ── Cleanup function ── */
    container._cleanupCarousel = function () {
      if (ac) ac.abort();
      if (resizeObs) resizeObs.disconnect();
      if (intersectionObs) intersectionObs.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(resizeTimer);
      clearTimeout(liveTimer);
      resizeObs = null;
      intersectionObs = null;
    };
  };

  /* Auto-cleanup on section unload (Shopify theme editor) */
  document.addEventListener('shopify:section:unload', function (e) {
    var container = e.target.querySelector('.mp-bib-carousel-container');
    if (container && container._cleanupCarousel) {
      container._cleanupCarousel();
    }
  });
})();
